import { Globe, Heart, Users, Lock } from "lucide-react";
import type { MekkeBookletVisibility } from "@tabipla/shared";

/**
 * Visibility configuration for booklet styling
 * Used in BookletCard, BookletDetail, etc.
 */
export interface VisibilityStyleConfig {
  icon: typeof Globe;
  badgeBg: string;
  gradientStyle: string;
  decorColor1: string;
  decorColor2: string;
  decorColor3: string;
}

export const VISIBILITY_CONFIG: Record<MekkeBookletVisibility, VisibilityStyleConfig> = {
  public: {
    icon: Globe,
    badgeBg: "bg-emerald-600",
    gradientStyle: "linear-gradient(135deg, #059669 0%, #0d9488 50%, #0891b2 100%)",
    decorColor1: "rgba(52, 211, 153, 0.4)",
    decorColor2: "rgba(94, 234, 212, 0.3)",
    decorColor3: "rgba(103, 232, 249, 0.2)",
  },
  followers_only: {
    icon: Heart,
    badgeBg: "bg-rose-600",
    gradientStyle: "linear-gradient(135deg, #e11d48 0%, #f43f5e 50%, #fb7185 100%)",
    decorColor1: "rgba(244, 63, 94, 0.4)",
    decorColor2: "rgba(251, 113, 133, 0.3)",
    decorColor3: "rgba(253, 164, 175, 0.2)",
  },
  limited: {
    icon: Users,
    badgeBg: "bg-violet-600",
    gradientStyle: "linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #d946ef 100%)",
    decorColor1: "rgba(167, 139, 250, 0.4)",
    decorColor2: "rgba(216, 180, 254, 0.3)",
    decorColor3: "rgba(240, 171, 252, 0.2)",
  },
  private: {
    icon: Lock,
    badgeBg: "bg-amber-700",
    gradientStyle: "linear-gradient(135deg, #d97706 0%, #f97316 50%, #f43f5e 100%)",
    decorColor1: "rgba(251, 191, 36, 0.4)",
    decorColor2: "rgba(253, 186, 116, 0.3)",
    decorColor3: "rgba(251, 113, 133, 0.2)",
  },
};

/**
 * Get visibility config with fallback to private
 */
export function getVisibilityConfig(
  visibility: MekkeBookletVisibility | undefined
): VisibilityStyleConfig {
  return VISIBILITY_CONFIG[visibility || "private"];
}

/**
 * Category gradient configuration for spot styling
 * Used in SpotCard, BookletDetail, SpotDetail, etc.
 */
export interface CategoryGradientConfig {
  gradient: string;
  decor1: string;
  decor2: string;
}

export const CATEGORY_GRADIENTS: Record<string, CategoryGradientConfig> = {
  restaurant: {
    gradient: "linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #f97316 100%)",
    decor1: "rgba(248, 113, 113, 0.4)",
    decor2: "rgba(251, 146, 60, 0.3)",
  },
  cafe: {
    gradient: "linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fbbf24 100%)",
    decor1: "rgba(251, 146, 60, 0.4)",
    decor2: "rgba(253, 224, 71, 0.3)",
  },
  landmark: {
    gradient: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a855f7 100%)",
    decor1: "rgba(167, 139, 250, 0.4)",
    decor2: "rgba(192, 132, 252, 0.3)",
  },
  park: {
    gradient: "linear-gradient(135deg, #059669 0%, #22c55e 50%, #84cc16 100%)",
    decor1: "rgba(74, 222, 128, 0.4)",
    decor2: "rgba(163, 230, 53, 0.3)",
  },
  museum: {
    gradient: "linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #06b6d4 100%)",
    decor1: "rgba(96, 165, 250, 0.4)",
    decor2: "rgba(34, 211, 238, 0.3)",
  },
  shop: {
    gradient: "linear-gradient(135deg, #db2777 0%, #ec4899 50%, #f472b6 100%)",
    decor1: "rgba(244, 114, 182, 0.4)",
    decor2: "rgba(251, 207, 232, 0.3)",
  },
  other: {
    gradient: "linear-gradient(135deg, #4b5563 0%, #6b7280 50%, #9ca3af 100%)",
    decor1: "rgba(156, 163, 175, 0.4)",
    decor2: "rgba(209, 213, 219, 0.3)",
  },
};

/**
 * Get category gradient config with fallback to 'other'
 */
export function getCategoryGradient(category: string): CategoryGradientConfig {
  return CATEGORY_GRADIENTS[category] || CATEGORY_GRADIENTS.other;
}

/**
 * Check if a booklet can be shared (limited or public with share code)
 */
export function canShareBooklet(
  visibility: MekkeBookletVisibility | undefined,
  shareCode: string | null | undefined
): boolean {
  return (visibility === "limited" || visibility === "public") && !!shareCode;
}

/**
 * Cover gradient config for public/shared booklet pages
 */
export const COVER_GRADIENT = {
  gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
  decor1: "rgba(165, 180, 252, 0.4)",
  decor2: "rgba(196, 181, 253, 0.3)",
  decor3: "rgba(216, 180, 254, 0.2)",
};
